import React, { useEffect, useRef, useState } from "react";
import { ValidationMessage, hasRole } from "../../components/Utils";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  COUNTRY_LIST_DROPDOWN,
  STATE_LIST_DROPDOWN,
  CITY_LIST_DROPDOWN,
  REGION_LIST_DROPDOWN,
  CHAPTER_LIST_DROPDOWN,
  ROLE_LIST_DROPDOWN,
} from "../../routes/routes";
import { toast } from "react-toastify";
import { FullPageLoader } from "../../components/common/Loader";
import Select from "react-select";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { usePermission } from "../../hooks/usePermission";
import {
  ADMIN_TYPES,
  GEO_LEVEL_META,
  canEditAdminType,
} from "./adminTypeConfig";

const EditAdmin = () => {
  const navigate = useNavigate();
  const { type: typeParam, id } = useParams();
  const { hasPermission } = usePermission();

  const config = ADMIN_TYPES[typeParam];
  const allowed =
    !!config && canEditAdminType(typeParam, hasPermission, hasRole);

  const [form, setForm] = useState(null);
  const [filter, setFilter] = useState({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [chapterOptions, setChapterOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [type, setType] = useState("password");
  const [type1, setType1] = useState("password");

  const selectStateRef = useRef(null);
  const selectCityRef = useRef(null);
  const selectRegionRef = useRef(null);
  const selectChapterRef = useRef(null);

  const clearSelect = (ref) => {
    if (ref.current?.clearValue) ref.current.clearValue();
  };
  const handleStateClear = () => {
    clearSelect(selectStateRef);
    clearSelect(selectCityRef);
    clearSelect(selectRegionRef);
    clearSelect(selectChapterRef);
  };
  const handleCityClear = () => {
    clearSelect(selectCityRef);
    clearSelect(selectRegionRef);
    clearSelect(selectChapterRef);
  };
  const handleRegionClear = () => {
    clearSelect(selectRegionRef);
    clearSelect(selectChapterRef);
  };
  const handleChapterClear = () => {
    clearSelect(selectChapterRef);
  };


  useEffect(() => {
    if (!allowed) {
      toast.error("Access denied for this admin type");
      navigate("/admins");
      return;
    }
    axios
      .get(config.api.edit + id, { params: { admin_type: config.key } })
      .then((res) => {
        if (res.status === 200 && res.data.success) {
          const responseData = res.data.data;
          const nextForm = {
            name: responseData?.name,
            email: responseData?.email,
            mobile_number: responseData?.mobile_number,
            mobile_country_code: responseData?.mobile_country_code || "91",
            admin_role_id: responseData?.admin_role_id,
            admin_role_id_selected: responseData?.admin_role_id
              ? {
                  value: Number(responseData?.admin_role_id),
                  label: responseData?.admin_role_name,
                }
              : null,
            access_ids: responseData?.access_ids || [],
            password: "",
            confirm_password: "",
          };

          config.geoLevels.forEach((level) => {
            const meta = GEO_LEVEL_META[level];
            const idVal = responseData?.[meta.filterKey];
            nextForm[meta.filterKey] = idVal || 0;
            nextForm[meta.selectedKey] = idVal
              ? {
                  value: Number(idVal),
                  label: responseData?.[meta.nameKey],
                }
              : null;
          });

          setForm(nextForm);
          setFilter({
            country_id: responseData?.country_id || null,
            state_id: responseData?.state_id || null,
            city_id: responseData?.city_id || null,
            region_id: responseData?.region_id || null,
            chapter_id: responseData?.chapter_id || null,
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, typeParam]);

  useEffect(() => {
    if (!allowed) return;
    const fetchData = async () => {
      try {
        const countryResponse = await axios.get(COUNTRY_LIST_DROPDOWN);
        setCountryOptions(countryResponse.data.data || []);

        const stateResponse = await axios.get(STATE_LIST_DROPDOWN, {
          params: { ...filter },
        });
        setStateOptions(stateResponse.data.data || []);

        const cityResponse = await axios.get(CITY_LIST_DROPDOWN, {
          params: { ...filter },
        });
        setCityOptions(cityResponse.data.data || []);

        const regionResponse = await axios.get(REGION_LIST_DROPDOWN, {
          params: { ...filter },
        });
        setRegionOptions(regionResponse.data.data || []);

        const chapterResponse = await axios.get(CHAPTER_LIST_DROPDOWN, {
          params: { ...filter },
        });
        setChapterOptions(chapterResponse.data.data || []);

        const roleResponse = await axios.get(ROLE_LIST_DROPDOWN, {
          params: config?.key ? { admin_type: config.key } : {},
        });
        setRoleOptions(roleResponse.data.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, [filter, allowed, config?.key]);

  const submit = () => {
    setBtnDisabled(true);
    let requestBody = { ...form, admin_type: config.key };
    axios
      .put(config.api.update + id, requestBody)
      .then((res) => {
        if (res.status === 200 && res.data.success) {
          toast.success(config.toasts.updated);
          navigate("/admins");
        }
      })
      .catch((err) => {
        const data = err.response?.data;
        if (data?.error && typeof data.error === "object" && !Array.isArray(data.error)) {
          setErrors(data.error);
        }
        toast.error(data?.msg || "Something went wrong");
      })
      .finally(() => {
        setBtnDisabled(false);
      });
  };

  const optionsByLevel = {
    country: countryOptions,
    state: stateOptions,
    city: cityOptions,
    region: regionOptions,
    chapter: chapterOptions,
  };
  const refByLevel = {
    state: selectStateRef,
    city: selectCityRef,
    region: selectRegionRef,
    chapter: selectChapterRef,
  };

  const accessParentKey = config
    ? GEO_LEVEL_META[config.geoLevels[config.geoLevels.length - 1]].filterKey
    : null;
  const accessOptions =
    config?.access && form?.[accessParentKey]
      ? optionsByLevel[config.access.level]
      : [];

  const onGeoChange = (level, selected) => {
    const meta = GEO_LEVEL_META[level];
    if (level === "country") {
      handleStateClear();
      setForm({
        ...form,
        country_id: selected?.value,
        state_id: null,
        city_id: null,
        region_id: null,
        chapter_id: null,
        country_id_selected: selected,
        state_id_selected: null,
        city_id_selected: null,
        region_id_selected: null,
        chapter_id_selected: null,
        access_ids: [],
      });
      setFilter({
        ...filter,
        country_id: selected?.value,
        state_id: null,
        city_id: null,
        region_id: null,
        chapter_id: null,
      });
      return;
    }
    if (level === "state") {
      handleCityClear();
      setForm({
        ...form,
        state_id: selected?.value,
        city_id: null,
        region_id: null,
        chapter_id: null,
        state_id_selected: selected,
        city_id_selected: null,
        region_id_selected: null,
        chapter_id_selected: null,
        access_ids: [],
      });
      setFilter({
        ...filter,
        state_id: selected?.value,
        city_id: null,
        region_id: null,
        chapter_id: null,
      });
      return;
    }
    if (level === "city") {
      handleRegionClear();
      setForm({
        ...form,
        city_id: selected?.value ? Number(selected.value) : null,
        region_id: null,
        chapter_id: null,
        city_id_selected: selected,
        region_id_selected: null,
        chapter_id_selected: null,
        access_ids: [],
      });
      setFilter({
        ...filter,
        city_id: selected?.value,
        region_id: null,
        chapter_id: null,
      });
      return;
    }
    if (level === "region") {
      handleChapterClear();
      setForm({
        ...form,
        region_id: selected?.value,
        chapter_id: null,
        region_id_selected: selected,
        chapter_id_selected: null,
        access_ids: [],
      });
      setFilter({
        ...filter,
        region_id: selected?.value,
        chapter_id: null,
      });
      return;
    }
    if (level === "chapter") {
      setForm({
        ...form,
        chapter_id: selected?.value,
        [meta.selectedKey]: selected,
      });
      setFilter({ ...filter, chapter_id: selected?.value });
    }
  };

  if (!allowed) return null;

  return (
    <div className="content-wrapper">
      <FullPageLoader visible={loading} />
      <div className="container-fluid p-0 mt-0 mb-2">
        <div className="col-lg-12">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h5 className="page-title">Admins</h5>
              <ol className="breadcrumb">
                <li>
                  <div className="breadcrumb_f">
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="50"
                        height="50"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#212b39"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div>
                      <Link to={"/dashboard"}>
                        <span>Dashboard</span>
                      </Link>
                    </div>
                  </div>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <Link to={"/admins"}>
                    <span>Admins List</span>
                  </Link>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span className="active_breadcrumb">
                    Edit {config.label}
                  </span>
                </li>
              </ol>
            </div>
            <div className="col-lg-6">
              <div className="pt-btn">
                <Link to={"/admins"}>
                  <button type="button" className="btn btn-dark igoback_btn">
                    <i className="bx bx-exit"></i> back
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-grow-1">
        <div className="row">
          <div className="col-xl-12">
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center" />
              <div className="card-body">
                {form && (
                  <form autoComplete="off">
                    <div className="row">
                      <div className="col-6 mb-3">
                        <label className="form-label">Admin Type</label>
                        <Select
                          isDisabled
                          options={[{ value: config.key, label: config.label }]}
                          value={{ value: config.key, label: config.label }}
                        />
                      </div>

                      {config.geoLevels.map((level) => {
                        const meta = GEO_LEVEL_META[level];
                        return (
                          <div className="col-6 mb-3" key={level}>
                            <label
                              htmlFor={meta.filterKey}
                              className="form-label"
                            >
                              {meta.label}
                              <span className="text-danger">*</span>
                            </label>
                            <Select
                              isDisabled={true}
                              isClearable={false}
                              ref={refByLevel[level] || undefined}
                              name={meta.filterKey}
                              options={optionsByLevel[level]}
                              value={
                                form?.[meta.selectedKey] ||
                                optionsByLevel[level]?.find(
                                  (opt) =>
                                    Number(opt.value) ===
                                    Number(form?.[meta.filterKey])
                                ) ||
                                null
                              }
                            />
                            <ValidationMessage
                              error={errors}
                              name={meta.filterKey}
                            />
                          </div>
                        );
                      })}

                      {config.access && (
                        <div className="col-6 mb-3">
                          <label htmlFor="access_ids" className="form-label">
                            {config.access.label}
                            <span className="text-danger">*</span>
                          </label>
                          <Select
                            isMulti={true}
                            name="access_ids"
                            options={accessOptions}
                            value={accessOptions.filter((opt) =>
                              (form?.access_ids || []).includes(
                                Number(opt.value)
                              )
                            )}
                            onChange={(selected) =>
                              setForm({
                                ...form,
                                access_ids: selected
                                  ? selected.map((opt) => Number(opt.value))
                                  : [],
                              })
                            }
                          />
                          <ValidationMessage
                            error={errors}
                            name="access_ids"
                          />
                        </div>
                      )}

                      <div className="col-6 mb-3">
                        <label htmlFor="admin_role_id" className="form-label">
                          Roles and Permission<span className="text-danger">*</span>
                        </label>
                        <Select
                          isClearable
                          options={roleOptions}
                          placeholder={
                            roleOptions.length === 0
                              ? "No roles found for this Admin Type"
                              : "Select"
                          }
                          value={
                            form?.admin_role_id
                              ? form?.admin_role_id_selected ||
                                roleOptions.find(
                                  (r) => Number(r.value) === Number(form.admin_role_id)
                                ) || null
                              : null
                          }
                          onChange={(selected) =>
                            setForm({
                              ...form,
                              admin_role_id: selected ? selected.value : null,
                              admin_role_id_selected: selected || null,
                            })
                          }
                        />
                        <ValidationMessage
                          error={errors}
                          name="admin_role_id"
                        />
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label" htmlFor="name">
                            Name<span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            id="name"
                            name="admin_name"
                            placeholder="Name"
                            autoComplete="off"
                            value={form?.name || ""}
                            onChange={(e) =>
                              setForm({ ...form, name: e.target.value })
                            }
                          />
                          <ValidationMessage error={errors} name="name" />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label" htmlFor="email">
                            Email<span className="text-danger">*</span>
                          </label>
                          <input
                            type="email"
                            className="form-control"
                            id="email"
                            name="admin_email"
                            placeholder="Email"
                            autoComplete="off"
                            value={form?.email || ""}
                            onChange={(e) =>
                              setForm({ ...form, email: e.target.value })
                            }
                          />
                          <ValidationMessage error={errors} name="email" />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label
                            className="form-label"
                            htmlFor="mobile_number"
                          >
                            Mobile Number
                            <span className="text-danger">*</span>
                          </label>
                          <PhoneInput
                            country="in"
                            preferredCountries={["in"]}
                            enableSearch={true}
                            searchPlaceholder="Search country or code..."
                            searchNotFound="No country found"
                            searchStyle={{
                              width: "88%",
                              padding: "6px 8px",
                              fontSize: "14px",
                            }}
                            countryCodeEditable={true}
                            value={`${form?.mobile_country_code || "91"}${
                              form?.mobile_number || ""
                            }`}
                            onChange={(value, country) => {
                              const dial = country?.dialCode
                                ? String(country.dialCode)
                                : form?.mobile_country_code || "91";
                              const rawNumber =
                                country?.dialCode && value.startsWith(dial)
                                ? value.slice(dial.length)
                                : value;
                              setForm({
                                ...form,
                                mobile_country_code: dial,
                                mobile_number: rawNumber,
                              });
                            }}
                            inputProps={{
                              id: "mobile_number",
                              name: "mobile_number",
                            }}
                            containerClass="w-100"
                            inputClass="form-control"
                            inputStyle={{ width: "100%" }}
                          />
                          <ValidationMessage
                            error={errors}
                            name="mobile_number"
                          />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label" htmlFor="password">
                            Password
                          </label>
                          <div className="input-group input-group-merge">
                            <input
                              type={type}
                              className="form-control"
                              id="password"
                              name="admin_password"
                              placeholder="***********"
                              autoComplete="new-password"
                              value={form?.password || ""}
                              onChange={(e) =>
                                setForm({ ...form, password: e.target.value })
                              }
                            />
                            {type === "password" ? (
                              <span
                                className="input-group-text cursor-pointer"
                                id="basic-default-password"
                                onClick={() => setType("text")}
                              >
                                <i className="bx bx-hide"></i>
                              </span>
                            ) : (
                              <span
                                className="input-group-text cursor-pointer"
                                id="basic-default-password"
                                onClick={() => setType("password")}
                              >
                                <i className="bx bx-show"></i>
                              </span>
                            )}
                          </div>
                          <ValidationMessage error={errors} name="password" />
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label
                            className="form-label"
                            htmlFor="confirm_password"
                          >
                            Confirm Password
                          </label>
                          <div className="input-group input-group-merge">
                            <input
                              type={type1}
                              className="form-control"
                              id="confirm_password"
                              name="admin_confirm_password"
                              placeholder="***********"
                              autoComplete="new-password"
                              value={form?.confirm_password || ""}
                              onChange={(e) =>
                                setForm({
                                  ...form,
                                  confirm_password: e.target.value,
                                })
                              }
                            />
                            {type1 === "password" ? (
                              <span
                                className="input-group-text cursor-pointer"
                                id="basic-default-password"
                                onClick={() => setType1("text")}
                              >
                                <i className="bx bx-hide"></i>
                              </span>
                            ) : (
                              <span
                                className="input-group-text cursor-pointer"
                                id="basic-default-password"
                                onClick={() => setType1("password")}
                              >
                                <i className="bx bx-show"></i>
                              </span>
                            )}
                          </div>
                          <ValidationMessage
                            error={errors}
                            name="confirm_password"
                          />
                        </div>
                      </div>

                      <div className="demo-inline-spacing d-flex justify-content-end">
                        <Link to={"/admins"}>
                          <button
                            type="button"
                            className="btn btn-primary iclose_btn"
                          >
                            Close
                          </button>
                        </Link>
                        <button
                          disabled={btnDisabled}
                          type="submit"
                          className="btn btn-success isubmit_button"
                          onClick={() => submit()}
                        >
                          &nbsp; Save
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditAdmin;
